import Cookies from 'universal-cookie';

var defaults = {
    reloadOnSave: true,
    categories: [
        { id: 'functional', required: true },
        { id: 'social' },
        { id: 'analytical' },
        { id: 'marketing' }
    ],
    expires: 365
};

var CookieConsent = function CookieConsent(Vue, config) {

    // 1. Set any configuration settings
    this.setConfig(config);

    // 2. Initialize the custom Vue instance for this plugin
    this.initVm(Vue);

    // 3. Make sure any required categories are checked right away.
    this.checkRequiredCategories();

    // 4. Call init hook
    this.initHook();
};


/**********************************************************
 * Config & Initialization
 **********************************************************/

/**
 * Initialize Vue instance to make data binding possible
 * without having to provide a VueX store instance.
 *
 * @param   {Object}  Vue  Vue object as passed to the plugin
 *
 * @return  {void}
 */
CookieConsent.prototype.initVm = function initVm (Vue) {
    this.cookieVm = new Vue({
        data: function data() {
            return {
                checkedCategories: [],
                dismissed: false,
                isSaving: false
            };
        }
    });

    this.initData();
};

CookieConsent.prototype.initData = function initData () {
    this.cookieVm.checkedCategories = this.getInitialCheckedCategories();
    this.cookieVm.dismissed = this.getInitialDismissedStatus();
};

/**
 * Set the plugin's configuration.
 * Each setting can be passed to the plugin's config,
 * and will use the fallback value when there's no custom value.
 *
 * @param   {Array}categoriesArray of category objects
 * @param   {Boolean}  reloadOnSave  Whether or not to reload the page on save
 * @param   {Number}   expires   Expery time of any set cookies
 * @param   {Request}   req   A node (express) request object
 * @return  {void}
 */
CookieConsent.prototype.setConfig = function setConfig (ref) {
        var categories = ref.categories;
        var reloadOnSave = ref.reloadOnSave;
        var expires = ref.expires;
        var onSave = ref.onSave;
        var onInit = ref.onInit;

    this.config = {};

    if (!categories || !Array.isArray(categories) || !categories.length) {
        this.config.categories = defaults.categories;
    } else {
        this.config.categories = categories;
    }

    this.config.expires = expires || defaults.expires;
    this.config.reloadOnSave = reloadOnSave || defaults.reloadOnSave;

    if (onSave && onSave instanceof Function) {
        this.config.onSave = onSave;
    }

    if (onInit && onInit instanceof Function) {
        this.config.onInit = onInit;
    }
};

/**
 * Set a request context so we can get cookies for SSR
 * @param {Request} req
 */
CookieConsent.prototype.setServerRequest = function setServerRequest (req) {
    this.req = req;
    // Reset data with info from request
    this.initData();
};

/**
 * Check if we have a server-side request context
 * @returns
 */
CookieConsent.prototype.hasCookieHeader = function hasCookieHeader () {
    return !!this.req && !!this.req.headers.cookie;
};

/**
 * Is the client-side window object available?
 */
CookieConsent.prototype.isClient = function isClient () {
    return typeof window !== 'undefined';
};

/**
 * Check any categories that are marked as required.
 *
 * This is mainly used for the 'functional' category.
 *
 * @return  {void}
 */
CookieConsent.prototype.checkRequiredCategories = function checkRequiredCategories () {
        var this$1$1 = this;

    this.getCategories().forEach(function (category) {
        if (this$1$1.isRequiredCategory(category.id)) {
            this$1$1.checkCategory(category.id);
        }
    });
};

CookieConsent.prototype.initHook = function initHook () {
    if (this.config.onInit && this.config.onInit instanceof Function) {
        this.config.onInit();
    }
};


/**********************************************************
 * Category related getters & checks
 **********************************************************/

/**
 * Get array of categories, as configured in the plugin.
 *
 * @return  {Array}  Array containing category objects
 */
CookieConsent.prototype.getCategories = function getCategories () {
    return this.config.categories;
};

/**
 * Get a single category object, by providing it's id.
 *
 * @param   {String}  categoryId  Category's unique ID
 *
 * @return  {Object}  Category object
 */
CookieConsent.prototype.getCategoryById = function getCategoryById (categoryId) {
    return this.getCategories().find(function (category) { return category.id === categoryId; });
};

/**
 * Check if the requested category is actually available.
 *
 * @param   {String}  categoryId  Category's unique ID
 *
 * @return  {Boolean}
 */
CookieConsent.prototype.isValidCategory = function isValidCategory (categoryId) {
    return !!this.getCategoryById(categoryId);
};

/**
 * Check if a specific category is always required.
 *
 * This means it cannot be unchecked.
 *
 * @param   {[type]}  categoryId  Category's unique ID
 *
 * @return  {Boolean}
 */
CookieConsent.prototype.isRequiredCategory = function isRequiredCategory (categoryId) {
    var category = this.getCategoryById(categoryId);

    if (!category) {
        return false;
    }

    return !!category.required;
};


/**********************************************************
 * Managing checked state internally (unsaved preferences)
 **********************************************************/

/**
 * Check if a category is currently checked
 *
 * @param   {String}  categoryId  ID of the category
 *
 * @return  {Boolean}
 */
CookieConsent.prototype.isChecked = function isChecked (categoryId) {
    return this.cookieVm.checkedCategories.includes(categoryId);
};

/**
 * Check a specific category
 *
 * Note: This isn't saved until calling the save method.
 *
 * @param   {String}  categoryId  ID of the category
 *
 * @return  {void}
 */
CookieConsent.prototype.checkCategory = function checkCategory (categoryId) {
    if (this.isChecked(categoryId) || !this.isValidCategory(categoryId)) {
        return;
    }

    this.cookieVm.checkedCategories.push(categoryId);
};

/**
 * Uncheck a specific category
 *
 * Note: This isn't saved until calling the save method.
 * Note: Categories marked as required cannot be unchecked.
 *
 * @param   {String}  categoryId  ID of the category
 *
 * @return  {void}
 */
CookieConsent.prototype.uncheckCategory = function uncheckCategory (categoryId) {
    var categoryIndex = this.cookieVm.checkedCategories.indexOf(categoryId);

    if (categoryIndex === -1 || this.isRequiredCategory(categoryId)) {
        return;
    }

    this.cookieVm.checkedCategories.splice(categoryIndex, 1);
};

/**
 * Check ALL available categories
 *
 * Note: This isn't saved until calling the save method.
 *
 * @return  {void}
 */
CookieConsent.prototype.checkAllCategories = function checkAllCategories () {
        var this$1$1 = this;

    this.getCategories().forEach(function (category) { return this$1$1.checkCategory(category.id); });
};


/**********************************************************
 * Saving state & setting actual cookies
 **********************************************************/

/**
 * Save all checked categories
 *
 * @return  {void}
 */
CookieConsent.prototype.save = function save () {
        var this$1$1 = this;

    this.getCategories().forEach(function (category) {
        var acceptedValue = 'no';

        if (this$1$1.isChecked(category.id)) {
            acceptedValue = 'yes';
        }

        this$1$1.setCookie(this$1$1.getCategoryCookieName(category.id), acceptedValue, {
            expires: this$1$1.getExpiryTime()
        });
    });

    if (this.config.onSave && this.config.onSave instanceof Function) {
        this.config.onSave();
    }

    this.dismissCookiebar();

    if (this.config.reloadOnSave && typeof window !== 'undefined') {
        this.cookieVm.isSaving = true;
        return window.location.reload();
    }
};

/**
 * Check if the save action was triggered.
 *
 * Note: This is only relevant when the reloadOnSave setting is set to true.
 *
 * @return  {Boolean}
 */
CookieConsent.prototype.isSaving = function isSaving () {
    if (!this.config.reloadOnSave) {
        return false;
    }

    return this.cookieVm.isSaving;
};

/**
 * Check if consent was given for a specific category
 *
 * @param   {String}  categoryId  Category ID
 *
 * @return  {Boolean}
 */
CookieConsent.prototype.hasConsent = function hasConsent (categoryId) {
    return this.getCookie(this.getCategoryCookieName(categoryId)) === 'yes';
};


/**********************************************************
 * Managing initial state (on page load)
 **********************************************************/

/**
 * Get the checked categories array as it was when
 * first loading the page. This value is derived
 * directly from the values of each category's cookie.
 *
 * The value may look something like the following:
 * ['analytical', 'social']
 *
 * @return  {Array}  Checked categories
 */
CookieConsent.prototype.getInitialCheckedCategories = function getInitialCheckedCategories () {
        var this$1$1 = this;

    var checkedCategories = [];

    this.getCategories().forEach(function (category) {
        if (this$1$1.hasConsent(category.id)) {
            checkedCategories.push(category.id);
        }
    });

    return checkedCategories;
};

/**
 * Get the dismissed status of the cookiebar, as it was on loading the page.
 * This value is derived directly from the cookiebar dismissed cookie.
 *
 * @return  {Boolean}  Initial dismissed status
 */
CookieConsent.prototype.getInitialDismissedStatus = function getInitialDismissedStatus () {
    return this.getCookie(this.getDismissedCookieName()) === 'yes';
};

/**
 * Check if the user has changed any of their cookie preferences
 * since loading the page initially.
 *
 * This can be used to display a message before closing a cookie modal,
 * only if the user's preferences have actually changed.
 *
 * @return  {Boolean}  Whether preferences changed or not
 */
CookieConsent.prototype.hasChanged = function hasChanged () {
        var this$1$1 = this;

    var lengthChanged = this.getInitialCheckedCategories().length !== this.cookieVm.checkedCategories.length;
    var hasChanged = false;

    if (lengthChanged) {
        return true;
    }

    this.cookieVm.checkedCategories.forEach(function (categoryId) {
        if (!this$1$1.getInitialCheckedCategories().includes(categoryId)) {
            hasChanged = true;
        }
    });

    return hasChanged;
};


/**********************************************************
 * Cookiebar dismissed state
 **********************************************************/

/**
 * Dismiss the cookiebar
 *
 * This is done by storing a cookie which can be parsed
 * each time the page is loaded. If the value is 'yes', the
 * cookiebar shouldn't be displayed.
 *
 * @return  {void}
 */
CookieConsent.prototype.dismissCookiebar = function dismissCookiebar () {

    this.setCookie('cookiebar_dismissed', 'yes', {
        expires: this.getExpiryTime()
    });

    this.cookieVm.dismissed = true;
};

/**
 * Check if cookiebar is dismissed
 *
 * @return  {Boolean}
 */
CookieConsent.prototype.isCookiebarDismissed = function isCookiebarDismissed () {
    return this.cookieVm.dismissed;
};


/**********************************************************
 * Internal API
 **********************************************************/

CookieConsent.prototype.getCookie = function getCookie (cookieName) {
    var cookies = new Cookies();
    // Use server request if client-side APIs are not available AND a request is set
    if (!this.isClient() && this.hasCookieHeader()) {
        cookies = new Cookies(this.req.headers.cookie);
    }

    return cookies.get(cookieName);
};

CookieConsent.prototype.setCookie = function setCookie (cookieName, value, config) {
    var cookies = new Cookies();
    // Use server request if client-side APIs are not available AND a request is set
    if (!this.isClient() && this.hasCookieHeader()) {
        cookies = new Cookies(this.req.headers.cookie);
    }
    config = config || {};
    // config.sameSite = 'Lax';
    // config.httpOnly = true;
    // config.secure = true;
    // config.domain = 'fairwear.org';
    // config.path = '/';

    cookies.set(cookieName, value, config);
};

CookieConsent.prototype.getCategoryCookieName = function getCategoryCookieName (categoryId) {
    return ("cookie_accept_" + categoryId);
};

CookieConsent.prototype.getDismissedCookieName = function getDismissedCookieName () {
    return 'cookiebar_dismissed';
};

CookieConsent.prototype.getExpiryTime = function getExpiryTime () {
    var date = new Date();
    var newDate = date.getDate() + this.config.expires;
    date.setDate(new Date(newDate));
    return date;
};

var cookiebar = {
render: function(){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('transition',{attrs:{"name":"cookiebar","appear":""}},[(_vm.showCookiebar)?_c('div',{staticClass:"dn-cookiebar",attrs:{"data-nosnippet":""}},[_c('div',{staticClass:"dn-cookiebar__body"},[_vm._t("default",null,{"acceptAllCookies":_vm.acceptAllCookies,"dismissCookiebar":_vm.dismissCookiebar,"isSaving":_vm.$cookieConsent.isSaving()})],2)]):_vm._e()])},
staticRenderFns: [],
    props: {
        isVisible: {
            type: Boolean,
            default: null
        }
    },

    data: function data() {
        return {
            isSaving: false
        };
    },

    computed: {
        showCookiebar: function showCookiebar() {
            if (this.isVisible === false || this.isVisible === true) {
                return this.isVisible;
            }

            return !this.$cookieConsent.isCookiebarDismissed();
        }
    },

    methods: {
        acceptAllCookies: function acceptAllCookies() {
            this.$cookieConsent.checkAllCategories();
            this.$cookieConsent.save();
        },

        dismissCookiebar: function dismissCookiebar() {
            this.$cookieConsent.dismissCookiebar();
        }
    }
};

var cookiePreferences = {
render: function(){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('form',{staticClass:"dn-cookie-preferences"},[_c('ul',{staticClass:"dn-cookie-preferences__categories"},_vm._l((_vm.categories),function(category){return _c('li',{key:category.id,staticClass:"dn-cookie-preferences__category",attrs:{"data-required":_vm.$cookieConsent.isRequiredCategory(category.id)}},[_vm._t("category_trigger",function(){return [_c('input',{directives:[{name:"model",rawName:"v-model",value:(_vm.$cookieConsent.cookieVm.checkedCategories),expression:"$cookieConsent.cookieVm.checkedCategories"}],attrs:{"id":category.id,"type":"checkbox","name":category.id},domProps:{"value":category.id,"checked":Array.isArray(_vm.$cookieConsent.cookieVm.checkedCategories)?_vm._i(_vm.$cookieConsent.cookieVm.checkedCategories,category.id)>-1:(_vm.$cookieConsent.cookieVm.checkedCategories)},on:{"change":function($event){var $$a=_vm.$cookieConsent.cookieVm.checkedCategories,$$el=$event.target,$$c=$$el.checked?(true):(false);if(Array.isArray($$a)){var $$v=category.id,$$i=_vm._i($$a,$$v);if($$el.checked){$$i<0&&(_vm.$set(_vm.$cookieConsent.cookieVm, "checkedCategories", $$a.concat([$$v])));}else {$$i>-1&&(_vm.$set(_vm.$cookieConsent.cookieVm, "checkedCategories", $$a.slice(0,$$i).concat($$a.slice($$i+1))));}}else {_vm.$set(_vm.$cookieConsent.cookieVm, "checkedCategories", $$c);}}}})]},{"category":category,"uncheck":_vm.$cookieConsent.uncheckCategory,"check":_vm.$cookieConsent.checkCategory,"isChecked":_vm.$cookieConsent.isChecked(category.id),"isRequired":_vm.$cookieConsent.isRequiredCategory(category.id)}),_vm._v(" "),_c('label',{staticClass:"dn-cookie-preferences__category-description",attrs:{"for":category.id}},[_vm._t(("category_" + (category.id)),function(){return [_vm._v("\n                    "+_vm._s(category.id)+"\n                ")]},{"category":category,"uncheck":_vm.$cookieConsent.uncheckCategory,"check":_vm.$cookieConsent.checkCategory,"isChecked":_vm.$cookieConsent.isChecked(category.id),"isRequired":_vm.$cookieConsent.isRequiredCategory(category.id)})],2)],2)}),0)])},
staticRenderFns: [],
    computed: {
        categories: function categories() {
            return this.$cookieConsent.getCategories();
        }
    },

    methods: {
        // @TODO: Use this instead of manually passing each prop to every slot.
        // Didn't get the chance to test this thoroughly yet.
        getSlotAttrs: function getSlotAttrs(category) {
            return {
                category: this.category,
                uncheck: this.$cookieConsent.uncheckCategory(category.id),
                check: this.$cookieConsent.checkCategory(category.id),
                isChecked: this.$cookieConsent.isChecked(category.id),
                isRequired: this.$cookieConsent.isRequiredCategory(category.id)
            };
        }
    }
};

// import DnCookieBlocker from './components/cookie-blocker/cookie-blocker.vue';

var VueCookieConsent = {
    install: function install(Vue, config) {
        if ( config === void 0 ) config = {};

        Vue.prototype.$cookieConsent = Vue.$cookieConsent = new CookieConsent(Vue, config);
    }
};

export { cookiePreferences as DnCookiePreferences, cookiebar as DnCookiebar, VueCookieConsent as default };
